import { Home, Package, Book, User } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

const BottomNav = () => {
    const location = useLocation();
    const { user, isSetupComplete, isAdmin } = useAuth();
    const [isMinimized, setIsMinimized] = useState(false);
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
    const lastScrollYRef = useRef(0);
    const scrollTimeoutRef = useRef(null);

    // The early return must be after all hooks to prevent Rules of Hooks violations.


    const navItems = [
        { to: "/", icon: Home, label: "Dashboard" },
        { to: "/home-records", icon: Package, label: "My Home", title: "My Home" },
        { to: "/directory", icon: Book, label: "Directory" },
        { to: "/profile", icon: User, label: "Profile" },
    ];

    useEffect(() => {
        const controlNavbar = () => {
            if (typeof window === 'undefined') return;

            const currentScrollY = window.scrollY;

            // Clear any existing timeout on every scroll event
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            // 1. Always expand if at the very top (buffer of 20px)
            if (currentScrollY < 20) {
                setIsMinimized(false);
                lastScrollYRef.current = currentScrollY;
                return;
            }

            // 2. Scrolling Logic
            if (currentScrollY > lastScrollYRef.current) {
                // Scrolling Down -> Minimize immediately
                setIsMinimized(true);
            } else if (currentScrollY < lastScrollYRef.current) {
                // Scrolling Up -> Expand temporarily
                setIsMinimized(false);
            }

            lastScrollYRef.current = currentScrollY;

            // 3. "Stop Scrolling" Check
            // If the user stops scrolling (and isn't at the top), minimize again after a delay.
            scrollTimeoutRef.current = setTimeout(() => {
                if (window.scrollY >= 20) {
                    setIsMinimized(true);
                }
            }, 1200); // 1.2s delay before minimizing on stop
        };

        window.addEventListener('scroll', controlNavbar);
        return () => {
            window.removeEventListener('scroll', controlNavbar);
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        let showListener, hideListener;

        if (Capacitor.isPluginAvailable('Keyboard')) {
            showListener = Keyboard.addListener('keyboardWillShow', () => {
                setIsKeyboardOpen(true);
            });
            hideListener = Keyboard.addListener('keyboardWillHide', () => {
                setIsKeyboardOpen(false);
            });
        }

        return () => {
            if (showListener) showListener.remove();
            if (hideListener) hideListener.remove();
        };
    }, []);

    // Hide BottomNav if the user is not logged in, hasn't finished setup, is on marketing pages,
    // if the keyboard is open, or (only while the private beta lock is active) if they're not an admin.
    // This mirrors ProtectedRoute.jsx's beta-lock gating so non-admin users regain navigation the
    // moment VITE_ENABLE_BETA_LOCK is turned off for public launch.
    const path = location.pathname.toLowerCase();
    const marketingPaths = ['/site', '/features', '/privacy', '/terms', '/cookie-policy', '/refund-policy', '/refund'];
    const isBetaLockActive = import.meta.env.VITE_ENABLE_BETA_LOCK === "true";
    if (!user || !isSetupComplete || marketingPaths.includes(path) || isKeyboardOpen || (isBetaLockActive && !isAdmin)) {
        return null;
    }

    return (
        <nav
            className={cn(
                "fixed left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]",
                isMinimized ? "bottom-4 w-auto" : "bottom-6 w-[90%] max-w-[400px]"
            )}
            onClick={() => setIsMinimized(false)}
        >
            <div className={cn(
                "bg-background/80 backdrop-blur-xl border border-white/20 shadow-2xl flex justify-between items-center ring-1 ring-black/5 transition-all duration-500",
                isMinimized
                    ? "rounded-full p-1.5 gap-1 bg-background/90"
                    : "rounded-3xl p-2"
            )}>
                {navItems.map((item) => {
                    const isActive = location.pathname === item.to || 
                        (item.to === "/" && location.pathname === "/app");
                    return (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            className={cn(
                                "flex items-center justify-center transition-all duration-300 ease-out",
                                isMinimized
                                    ? "rounded-full p-2 hover:bg-accent/50"
                                    : "rounded-2xl gap-2 px-4 py-3",
                                isActive && !isMinimized
                                    ? "bg-primary text-primary-foreground font-semibold shadow-md translate-y-[-2px]"
                                    : isActive && isMinimized
                                        ? "text-primary bg-primary/10"
                                        : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                            )}
                            title={item.title}
                        >
                            <item.icon className={cn(
                                "transition-all duration-300",
                                isMinimized ? "w-4 h-4" : "w-5 h-5"
                            )} />

                            <span className={cn(
                                "overflow-hidden whitespace-nowrap transition-all duration-300 text-sm",
                                isActive && !isMinimized ? "max-w-[100px] opacity-100 ml-1" : "max-w-0 opacity-0"
                            )}>
                                {item.label}
                            </span>
                        </NavLink>
                    );
                })}
            </div>
        </nav>
    );
};

export default BottomNav;
